/**
 * Task 18 Phase F — File-missing-post-deploy modal.
 *
 * Triggered whenever the FE catches a 410 Gone with detail.code ===
 * "file_missing_post_deploy", or when it sees a doc-level
 * file_missing_post_deploy: true flag. Replaces the cold red
 * "Request failed with status code 404" toast.
 *
 * Provides two CTAs:
 *   - Send re-upload email to customer
 *     → POST /api/admin/customers/{id}/file-reupload-reminder
 *       body { file_refs: [{label, kind}], note }
 *   - Close
 *
 * Caller wires open / close + the file metadata. The hook
 * (useMissingFileGuard) below detects the 410 from any axios response
 * and opens the modal automatically.
 */
import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Mail } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export function FileMissingPostDeployModal({
    open,
    onOpenChange,
    customerId,
    filename,
    fileRefs = null,    // optional [{ label, kind }] payload sent to BE
    onSent,
}) {
    const [note, setNote] = useState("");
    const [sending, setSending] = useState(false);

    const onSend = async () => {
        if (!customerId) {
            toast.error("Cannot send re-upload reminder — customer is unknown for this file.");
            return;
        }
        setSending(true);
        try {
            await api.post(`/admin/customers/${customerId}/file-reupload-reminder`, {
                file_refs: fileRefs || (filename ? [{ label: filename, kind: "unknown" }] : []),
                note: (note || "").trim() || null,
            });
            toast.success("Re-upload reminder sent.");
            onOpenChange(false);
            setNote("");
            onSent?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="file-missing-post-deploy-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-amber-900">
                        <AlertTriangle size={18} />
                        File no longer available
                    </DialogTitle>
                    <DialogDescription>
                        This file{filename ? <> (<span className="font-mono">{filename}</span>)</> : null} was uploaded
                        before the latest deployment migration and is no longer available. The customer
                        can re-upload via the customer portal, or you can request a re-upload below.
                    </DialogDescription>
                </DialogHeader>
                {customerId && (
                    <div className="space-y-1.5 mt-2">
                        <Label htmlFor="reupload-note">Add a short note to the email (optional)</Label>
                        <Textarea
                            id="reupload-note"
                            rows={3}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="e.g. We need an updated W-9 and COI to finish onboarding."
                            data-testid="reupload-note-input"
                        />
                    </div>
                )}
                <DialogFooter className="gap-2 sm:gap-2">
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        data-testid="file-missing-close"
                    >
                        Close
                    </Button>
                    {customerId && (
                        <Button
                            onClick={onSend}
                            disabled={sending}
                            className="bg-blue-900 hover:bg-blue-800"
                            data-testid="file-missing-send-reupload"
                        >
                            <Mail size={14} className="mr-1.5" />
                            {sending ? "Sending…" : "Send re-upload email to customer"}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default FileMissingPostDeployModal;
